import dt from 'dayjs'
import type { IAccount, IAccountAuthLocal, IRole, TRoleAccess, Part } from '../types'
import { ERoleAccess} from '../types'

export class Rule {
	public static $(rules: Array<TRoleAccess>, $key: ERoleAccess): TRoleAccess | null {
		return rules.find((_) => _.$key === $key) ?? null
	}
	public static $omit(rules: Array<TRoleAccess>, $keys: ERoleAccess[]): Array<TRoleAccess> {
		return rules.filter((_) => !$keys.includes(_.$key))
	}
	public static $pick(rules: Array<TRoleAccess>, $keys: ERoleAccess[]): Array<TRoleAccess> {
		return rules.filter((_) => $keys.includes(_.$key))
	}
	constructor(public readonly $rules: TRoleAccess[]) {}
	public get $(): Map<ERoleAccess, boolean | null> {
		const data: Array<[ERoleAccess, boolean | null]> = [...Object.values(ERoleAccess)].map(($key) => {
			return [$key, Rule.$(this.$rules, $key)?.$value ?? null]
		})
		return new Map<ERoleAccess, boolean | null>(data)
	}
	public $$($value: ERoleAccess): boolean | null {
		for (const [k, v] of Object.entries(ERoleAccess)) {
			if (v !== $value) continue
			if (!(k in this) || k === '$' || k === '$$') continue
			const $key = k as Exclude<keyof Rule, '$' | '$$'>
			if (!(typeof this[$key] === 'function')) continue
			const $calc = this[$key]
			// @ts-ignore
			return $calc()
		}
		return null
	}

	// Any (1)
	public Any = (): boolean => this.$.get(ERoleAccess.Any) ?? false
	// Api (2)
	public ApiHealthCheck = (): boolean => this.$.get(ERoleAccess.ApiHealthCheck) ?? this.Any()
	public ApiPostbackSend = (): boolean => this.$.get(ERoleAccess.ApiPostbackSend) ?? this.Any()
	// Auth (2)
	public AuthLogin = (): boolean => this.$.get(ERoleAccess.AuthLogin) ?? this.Any()
	public AuthProfile = (): boolean => this.$.get(ERoleAccess.AuthProfile) ?? this.Any()
	// Common (3)
	public CommonCountryAny = (): boolean => this.$.get(ERoleAccess.CommonCountryAny) ?? this.Any()
	public CommonCountryList = (): boolean => this.$.get(ERoleAccess.CommonCountryList) ?? this.CommonCountryAny()
	public CommonCountryRead = (): boolean => this.$.get(ERoleAccess.CommonCountryRead) ?? this.CommonCountryAny()
	// Common (3)
	public CommonLanguageAny = (): boolean => this.$.get(ERoleAccess.CommonLanguageAny) ?? this.Any()
	public CommonLanguageList = (): boolean => this.$.get(ERoleAccess.CommonLanguageList) ?? this.CommonLanguageAny()
	public CommonLanguageRead = (): boolean => this.$.get(ERoleAccess.CommonLanguageRead) ?? this.CommonLanguageAny()
	// Common (3)
	public CommonTimeZoneAny = (): boolean => this.$.get(ERoleAccess.CommonTimeZoneAny) ?? this.Any()
	public CommonTimeZoneList = (): boolean => this.$.get(ERoleAccess.CommonTimeZoneList) ?? this.CommonTimeZoneAny()
	public CommonTimeZoneRead = (): boolean => this.$.get(ERoleAccess.CommonTimeZoneRead) ?? this.CommonTimeZoneAny()
	// Role (6)
	public RoleAny = (): boolean => this.$.get(ERoleAccess.RoleAny) ?? this.Any()
	public RoleList = (): boolean => this.$.get(ERoleAccess.RoleList) ?? this.RoleAny()
	public RoleRead = (): boolean => this.$.get(ERoleAccess.RoleRead) ?? this.RoleAny()
	public RoleCreate = (): boolean => this.$.get(ERoleAccess.RoleCreate) ?? this.RoleAny()
	public RoleModify = (): boolean => this.$.get(ERoleAccess.RoleModify) ?? this.RoleAny()
	public RoleRemove = (): boolean => this.$.get(ERoleAccess.RoleRemove) ?? this.RoleAny()
	// Account (8)
	public AccountAny = (): boolean => this.$.get(ERoleAccess.AccountAny) ?? this.Any()
	public AccountList = (): boolean => this.$.get(ERoleAccess.AccountList) ?? this.AccountAny()
	public AccountIsFiredList = (): boolean => this.$.get(ERoleAccess.AccountIsFiredList) ?? this.AccountAny()
	public AccountRead = (): boolean => this.$.get(ERoleAccess.AccountRead) ?? this.AccountAny()
	public AccountCreate = (): boolean => this.$.get(ERoleAccess.AccountCreate) ?? this.AccountAny()
	public AccountModify = (): boolean => this.$.get(ERoleAccess.AccountModify) ?? this.AccountAny()
	public AccountRemove = (): boolean => this.$.get(ERoleAccess.AccountRemove) ?? this.AccountAny()
	public AccountResurrect = (): boolean => this.$.get(ERoleAccess.AccountResurrect) ?? false
	// AccountBuyer (3)
	public AccountBuyerAny = (): boolean => this.$.get(ERoleAccess.AccountBuyerAny) ?? this.AccountAny()
	public AccountBuyerSelf = (): boolean => this.$.get(ERoleAccess.AccountBuyerSelf) ?? false
	public AccountBuyerList = (): boolean => this.$.get(ERoleAccess.AccountBuyerList) ?? this.AccountBuyerAny()
	// AccountTeam (3)
	public AccountTeamAny = (): boolean => this.$.get(ERoleAccess.AccountTeamAny) ?? this.AccountAny()
	public AccountTeamSelf = (): boolean => this.$.get(ERoleAccess.AccountTeamSelf) ?? false
	public AccountTeamList = (): boolean => this.$.get(ERoleAccess.AccountTeamList) ?? this.AccountTeamAny()
	// Affiliate (6)
	public AffiliateAny = (): boolean => this.$.get(ERoleAccess.AffiliateAny) ?? this.Any()
	public AffiliateList = (): boolean => this.$.get(ERoleAccess.AffiliateList) ?? this.AffiliateAny()
	public AffiliateRead = (): boolean => this.$.get(ERoleAccess.AffiliateRead) ?? this.AffiliateAny()
	public AffiliateCreate = (): boolean => this.$.get(ERoleAccess.AffiliateCreate) ?? this.AffiliateAny()
	public AffiliateModify = (): boolean => this.$.get(ERoleAccess.AffiliateModify) ?? this.AffiliateAny()
	public AffiliateRemove = (): boolean => this.$.get(ERoleAccess.AffiliateRemove) ?? this.AffiliateAny()
	// Offer scope (6)
	public OfferScopeAny = (): boolean => this.$.get(ERoleAccess.OfferScopeAny) ?? this.Any()
	public OfferScopeList = (): boolean => this.$.get(ERoleAccess.OfferScopeList) ?? this.OfferScopeAny()
	public OfferScopeRead = (): boolean => this.$.get(ERoleAccess.OfferScopeRead) ?? this.OfferScopeAny()
	public OfferScopeCreate = (): boolean => this.$.get(ERoleAccess.OfferScopeCreate) ?? this.OfferScopeAny()
	public OfferScopeModify = (): boolean => this.$.get(ERoleAccess.OfferScopeModify) ?? this.OfferScopeAny()
	public OfferScopeRemove = (): boolean => this.$.get(ERoleAccess.OfferScopeRemove) ?? this.OfferScopeAny()
	// Offer (10)
	public OfferAny = (): boolean => this.$.get(ERoleAccess.OfferAny) ?? this.Any()
	public OfferAnyGroup = (): boolean => this.$.get(ERoleAccess.OfferAnyGroup) ?? this.OfferAny()
	public OfferList = (): boolean => this.$.get(ERoleAccess.OfferList) ?? this.OfferAny()
	public OfferRead = (): boolean => this.$.get(ERoleAccess.OfferRead) ?? this.OfferAny()
	public OfferCreate = (): boolean => this.$.get(ERoleAccess.OfferCreate) ?? this.OfferAny()
	public OfferModify = (): boolean => this.$.get(ERoleAccess.OfferModify) ?? this.OfferAny()
	public OfferRemove = (): boolean => this.$.get(ERoleAccess.OfferRemove) ?? this.OfferAny()
    public OfferResurrect = (): boolean => this.$.get(ERoleAccess.OfferResurrect) ?? false
	public OfferBind = (): boolean => this.$.get(ERoleAccess.OfferBind) ?? this.OfferAny()
	public OfferUnbind = (): boolean => this.$.get(ERoleAccess.OfferUnbind) ?? this.OfferAny()
	// OfferSpend (9)
	public OfferSpendAny = (): boolean => this.$.get(ERoleAccess.OfferSpendAny) ?? this.Any()
	public OfferSpendAnyAccount = (): boolean => this.$.get(ERoleAccess.OfferSpendAnyAccount) ?? this.OfferSpendAny()
	public OfferSpendAnyDay = (): boolean => this.$.get(ERoleAccess.OfferSpendAnyDay) ?? this.OfferSpendAny()
	public OfferSpendAnyGroup = (): boolean => this.$.get(ERoleAccess.OfferSpendAnyGroup) ?? this.OfferSpendAny()
	public OfferSpendList = (): boolean => this.$.get(ERoleAccess.OfferSpendList) ?? this.OfferSpendAny()
	public OfferSpendRead = (): boolean => this.$.get(ERoleAccess.OfferSpendRead) ?? this.OfferSpendAny()
	public OfferSpendCreate = (): boolean => this.$.get(ERoleAccess.OfferSpendCreate) ?? this.OfferSpendAny()
	public OfferSpendModify = (): boolean => this.$.get(ERoleAccess.OfferSpendModify) ?? this.OfferSpendAny()
	public OfferSpendRemove = (): boolean => this.$.get(ERoleAccess.OfferSpendRemove) ?? this.OfferSpendAny()
	// OfferSpendBy (4)
	public OfferSpendByAny = (): boolean => this.$.get(ERoleAccess.OfferSpendByAny) ?? this.OfferSpendAny()
	public OfferSpendByAccountList = (): boolean => this.$.get(ERoleAccess.OfferSpendByAccountList) ?? this.OfferSpendByAny()
	public OfferSpendByOfferList = (): boolean => this.$.get(ERoleAccess.OfferSpendByOfferList) ?? this.OfferSpendByAny()
	public OfferSpendByOfferScopeList = (): boolean => this.$.get(ERoleAccess.OfferSpendByOfferScopeList) ?? this.OfferSpendByAny()
	// Revenue (6)
	public RevenueAny = (): boolean => this.$.get(ERoleAccess.RevenueAny) ?? this.Any()
	public RevenueList = (): boolean => this.$.get(ERoleAccess.RevenueList) ?? this.RevenueAny()
	public RevenueRead = (): boolean => this.$.get(ERoleAccess.RevenueRead) ?? this.RevenueAny()
	public RevenueCreate = (): boolean => this.$.get(ERoleAccess.RevenueCreate) ?? this.RevenueAny()
	public RevenueModify = (): boolean => this.$.get(ERoleAccess.RevenueModify) ?? this.RevenueAny()
	public RevenueRemove = (): boolean => this.$.get(ERoleAccess.RevenueRemove) ?? this.RevenueAny()
	// RevenueAgency (6)
	public RevenueAgencyAny = (): boolean => this.$.get(ERoleAccess.RevenueAgencyAny) ?? this.RevenueAny()
	public RevenueAgencyList = (): boolean => this.$.get(ERoleAccess.RevenueAgencyList) ?? this.RevenueAgencyAny()
	public RevenueAgencyRead = (): boolean => this.$.get(ERoleAccess.RevenueAgencyRead) ?? this.RevenueAgencyAny()
	public RevenueAgencyCreate = (): boolean => this.$.get(ERoleAccess.RevenueAgencyCreate) ?? this.RevenueAgencyAny()
	public RevenueAgencyModify = (): boolean => this.$.get(ERoleAccess.RevenueAgencyModify) ?? this.RevenueAgencyAny()
	public RevenueAgencyRemove = (): boolean => this.$.get(ERoleAccess.RevenueAgencyRemove) ?? this.RevenueAgencyAny()
	// RevenueSpend (8)
	public RevenueSpendAny = (): boolean => this.$.get(ERoleAccess.RevenueSpendAny) ?? this.RevenueAny()
	public RevenueSpendAnyAccount = (): boolean => this.$.get(ERoleAccess.RevenueSpendAnyAccount) ?? this.RevenueSpendAny()
	public RevenueSpendAnyDay = (): boolean => this.$.get(ERoleAccess.RevenueSpendAnyDay) ?? this.RevenueSpendAny()
	public RevenueSpendList = (): boolean => this.$.get(ERoleAccess.RevenueSpendList) ?? this.RevenueSpendAny()
	public RevenueSpendRead = (): boolean => this.$.get(ERoleAccess.RevenueSpendRead) ?? this.RevenueSpendAny()
	public RevenueSpendCreate = (): boolean => this.$.get(ERoleAccess.RevenueSpendCreate) ?? this.RevenueSpendAny()
	public RevenueSpendModify = (): boolean => this.$.get(ERoleAccess.RevenueSpendModify) ?? this.RevenueSpendAny()
	public RevenueSpendRemove = (): boolean => this.$.get(ERoleAccess.RevenueSpendRemove) ?? this.RevenueSpendAny()
	// RevenueSpendBy (5)
	public RevenueSpendByAny = (): boolean => this.$.get(ERoleAccess.RevenueSpendByAny) ?? this.RevenueSpendAny()
	public RevenueSpendByAccountList = (): boolean => this.$.get(ERoleAccess.RevenueSpendByAccountList) ?? this.RevenueSpendByAny()
	public RevenueSpendByAccountsList = (): boolean => this.$.get(ERoleAccess.RevenueSpendByAccountsList) ?? this.RevenueSpendByAny()
	public RevenueSpendByTeamList = (): boolean => this.$.get(ERoleAccess.RevenueSpendByTeamList) ?? this.RevenueSpendByAny()
	public RevenueSpendByTeamsList = (): boolean => this.$.get(ERoleAccess.RevenueSpendByTeamsList) ?? this.RevenueSpendByAny()
	// Debug (4)
	public DebugAny = (): boolean => this.$.get(ERoleAccess.DebugAny) ?? false
	public DebugCronRunner = (): boolean => this.$.get(ERoleAccess.DebugCronRunner) ?? this.DebugAny()
	public DebugDraft = (): boolean => this.$.get(ERoleAccess.DebugDraft) ?? this.DebugAny()
	public DebugMessage = (): boolean => this.$.get(ERoleAccess.DebugMessage) ?? this.DebugAny()
	// Job (6)
	public JobAny = (): boolean => this.$.get(ERoleAccess.JobAny) ?? this.DebugAny()
	public JobList = (): boolean => this.$.get(ERoleAccess.JobList) ?? this.JobAny()
	public JobRead = (): boolean => this.$.get(ERoleAccess.JobRead) ?? this.JobAny()
	public JobCreate = (): boolean => this.$.get(ERoleAccess.JobCreate) ?? this.JobAny()
	public JobModify = (): boolean => this.$.get(ERoleAccess.JobModify) ?? this.JobAny()
	public JobRemove = (): boolean => this.$.get(ERoleAccess.JobRemove) ?? this.JobAny()
	// Project (6)
	public ProjectAny = (): boolean => this.$.get(ERoleAccess.ProjectAny) ?? this.DebugAny()
	public ProjectList = (): boolean => this.$.get(ERoleAccess.ProjectList) ?? this.ProjectAny()
	public ProjectRead = (): boolean => this.$.get(ERoleAccess.ProjectRead) ?? this.ProjectAny()
	public ProjectCreate = (): boolean => this.$.get(ERoleAccess.ProjectCreate) ?? this.ProjectAny()
	public ProjectModify = (): boolean => this.$.get(ERoleAccess.ProjectModify) ?? this.ProjectAny()
	public ProjectRemove = (): boolean => this.$.get(ERoleAccess.ProjectRemove) ?? this.ProjectAny()
	//
}

export class Access {
	constructor(
        private readonly account?: IAccount | null // Account
    ) {}
	public $account(): IAccount | null {
        return this.account ?? null
    }
	public $role(): Part<IRole, 'createdAt' | 'updatedAt'> | null {
        return this.$account()?.role ?? null
    }
	public $access(): TRoleAccess[] {
        return this.$role()?.access ?? []
    }
	public $auth(): Part<IAccountAuthLocal, 'updatedAt'> | null {
        return this.$account()?.auth ?? null
    }
	public $rule(): Rule {
        return new Rule(this.$access())
    }
	public $($value: ERoleAccess): boolean | null {
        return this.$rule().$$($value)
    }
    public get $check(): Checker {
        return new Checker(this)
    }
	//
	public inGroup(): string | null {
		const inGroup: string | null = this.$role()?.extra?.inGroup ?? null
		return this.$(ERoleAccess.OfferAnyGroup) ? null : inGroup
	}
	public isTeam(): Array<string> | null {
		const isTeam: string | null = this.$role()?.extra?.isTeam ?? null
		const values: Array<string> | null = isTeam?.split(',').filter((_) => !!_.trim()) ?? null
		return this.$(ERoleAccess.AccountBuyerAny) ? null : values
	}
	public isBuyer(): boolean {
		const isBuyer: boolean | null = this.$role()?.extra.isBuyer ?? null
		return isBuyer ?? false
	}
}
class Checker {
    constructor(
        private readonly access: Access // access
    ) {}
    public inGroup(group?: string): boolean {
        const inGroup: string = this.access.inGroup() ?? ''
        if (!inGroup) return true
        return group === inGroup
    }
    public isOwner(ownerId?: number): boolean {
        const accountId: number = this.access.$account()?.id ?? 0
        return accountId === ownerId
    }
    public offerSpendAt(at?: Date | dt.Dayjs): boolean {
        const day: dt.Dayjs | undefined = at && dt.utc(at).startOf('day')
        if (!day) return false
        const diff: number = dt.utc(new Date()).startOf('day').diff(day, 'days')
        return -3 <= diff && diff <= 0
    }
}